import JsBarcode from 'jsbarcode'

/**
 * 라벨·바코드 인쇄용 HTML 조립(1.0 `RGRTemplate.js` 이식).
 *
 * 1.0 은 `<RGRTemplate>` 을 항상 DOM 에 그려 두고 `getElementsByClassName('return_label_space')[0]
 * .innerHTML` 로 인쇄 시점에 읽었다. 이 이식은 숨은 컴포넌트를 마운트해 두는 대신 순수 함수로
 * HTML 문자열을 직접 만든다 — qz-tray 가 필요로 하는 건 어차피 문자열이고, 이쪽이 단위 테스트가 쉽다.
 * 바코드는 1.0 의 `react-barcode` 대신 이 레포에 이미 있는 `jsbarcode` 로 그린다(신규 의존성 최소화,
 * `pages/master/productManagement/components/BarcodePrintModal.tsx` 선례와 같은 패턴).
 */

const escapeHtml = (value: string): string =>
  value.replaceAll(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c)

interface BarcodeSvgOptions {
  width: number
  height: number
  fontSize: number
}

const barcodeSvg = (value: string, options: BarcodeSvgOptions): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  try {
    JsBarcode(svg, value, { format: 'CODE128', displayValue: true, textAlign: 'center', textPosition: 'bottom', ...options })
  } catch {
    return `<div style="color:red;font-size:12px;">잘못된 바코드: ${escapeHtml(value)}</div>`
  }
  return svg.outerHTML
}

export interface LabelPrintData {
  /** 등록센터(1.0 `RTN_WH_NM`). */
  centerNm: string
  /** 검수시간(1.0 `NOW_DATE`). */
  nowDate: string
  /** 반품번호(1.0 `ORD_NO`, 저장 시점 값으로 덮어써진다 — 1.0 `ORD_NO_NOW`). */
  ordNo: string
  orgInvoiceNo: string
  rtnInvoiceNo: string
  sprNm: string
  gdsNm: string
  gdsOpt: string
  barcode: string
  /** 정상/불량(1.0 `IS_NORMAL`). */
  isNormal: string
}

/** 라벨 인쇄 HTML — 저장 직후 자동 인쇄 대상(1.0 `return_label_space`). */
export const buildLabelHtml = (data: LabelPrintData): string => {
  const rows: [string, string][] = [
    ['등록센터', data.centerNm],
    ['검수시간', data.nowDate],
    ['반품번호', data.ordNo],
    ['원송장번호', data.orgInvoiceNo],
    ['반송장번호', data.rtnInvoiceNo],
    ['화주사', data.sprNm],
    ['상품명', data.gdsNm],
    ['옵션명', data.gdsOpt],
  ]
  const rowsHtml = rows
    .map(
      ([label, value]) => `
    <div style="display:flex;width:100%;margin:0 0 5px 0;font-family:Arial, Helvetica, sans-serif;">
      <div style="width:25%;margin:0 0 0 10px;font-size:12px;font-family:Arial, Helvetica, sans-serif;">${escapeHtml(label)}</div>
      <div style="width:75%;margin:0 10px 0 10px;font-size:12px;font-family:Arial, Helvetica, sans-serif;">${escapeHtml(value)}</div>
    </div>`,
    )
    .join('')

  return `
    <div style="display:flex;align-items:center;justify-content:center;margin-top:30px;width:100%;font-family:Arial, Helvetica, sans-serif;">
      <div style="width:100%;height:160px;margin-top:40px;display:flex;font-family:Arial, Helvetica, sans-serif;">
        <div style="width:85%;height:160px;font-family:Arial, Helvetica, sans-serif;">${rowsHtml}</div>
        <div style="width:15%;">
          <div style="font-size:23px;font-weight:bold;padding:2px;width:100%;text-align:center;border:1px solid black;">${escapeHtml(data.isNormal)}</div>
        </div>
      </div>
      <div style="width:100%;display:flex;justify-content:center;">
        ${barcodeSvg(data.barcode, { width: 2, height: 50, fontSize: 13 })}
      </div>
    </div>`
}

/** 바코드 재발행 HTML(1.0 `return_barcode_space`) — 상품바코드 재발행 버튼·바코드 출력 모달 대상. */
export const buildBarcodeHtml = (barcode: string): string => `
    <div style="display:flex;align-items:center;justify-content:center;margin-top:-40px;width:100%;font-family:Arial, Helvetica, sans-serif;">
      <div style="width:100%;margin-top:-20px;display:flex;font-family:Arial, Helvetica, sans-serif;">
        ${barcodeSvg(barcode, { width: 3.2, height: 100, fontSize: 25 })}
      </div>
    </div>`
